import { Injectable } from '@angular/core';

export interface TimeCurrencyInfo {
  currency: string;
  code: string;
  symbol: string;
  name: string;
  country: string;
  timezone: string;
  timezoneAbbr: string;
  minorUnit?: string;
}

@Injectable()
export class TimeCurrencyService {

  private timeCurrencyMapping: { [key: string]: TimeCurrencyInfo } = {
    // ASEAN Countries (Primary focus)
    'Indonesia': {
      currency: 'IDR',
      code: '360',
      symbol: 'Rp',
      name: 'Indonesian Rupiah',
      country: 'Indonesia',
      timezone: 'Asia/Jakarta',
      timezoneAbbr: 'WIB',
      minorUnit: 'Sen'
    },
    'Singapore': {
      currency: 'SGD',
      code: '702',
      symbol: 'S$',
      name: 'Singapore Dollar',
      country: 'Singapore',
      timezone: 'Asia/Singapore',
      timezoneAbbr: 'SGT',
      minorUnit: 'Cent'
    },
    'Malaysia': {
      currency: 'MYR',
      code: '458',
      symbol: 'RM',
      name: 'Malaysian Ringgit',
      country: 'Malaysia',
      timezone: 'Asia/Kuala_Lumpur',
      timezoneAbbr: 'MYT',
      minorUnit: 'Sen'
    },
    'Thailand': {
      currency: 'THB',
      code: '764',
      symbol: '฿',
      name: 'Thai Baht',
      country: 'Thailand',
      timezone: 'Asia/Bangkok',
      timezoneAbbr: 'ICT',
      minorUnit: 'Satang'
    },
    'Vietnam': {
      currency: 'VND',
      code: '704',
      symbol: '₫',
      name: 'Vietnamese Dong',
      country: 'Vietnam',
      timezone: 'Asia/Ho_Chi_Minh',
      timezoneAbbr: 'ICT',
      minorUnit: 'Hào'
    },
    'Philippines': {
      currency: 'PHP',
      code: '608',
      symbol: '₱',
      name: 'Philippine Peso',
      country: 'Philippines',
      timezone: 'Asia/Manila',
      timezoneAbbr: 'PHT',
      minorUnit: 'Sentimo'
    },
    'Myanmar': {
      currency: 'MMK',
      code: '104',
      symbol: 'K',
      name: 'Myanmar Kyat',
      country: 'Myanmar',
      timezone: 'Asia/Yangon',
      timezoneAbbr: 'MMT',
      minorUnit: 'Pya'
    },
    'Brunei': {
      currency: 'BND',
      code: '096',
      symbol: 'B$',
      name: 'Brunei Dollar',
      country: 'Brunei',
      timezone: 'Asia/Brunei',
      timezoneAbbr: 'BNT',
      minorUnit: 'Sen'
    },
    'Cambodia': {
      currency: 'KHR',
      code: '116',
      symbol: '៛',
      name: 'Cambodian Riel',
      country: 'Cambodia',
      timezone: 'Asia/Phnom_Penh',
      timezoneAbbr: 'ICT',
      minorUnit: 'Sen'
    },
    'Laos': {
      currency: 'LAK',
      code: '418',
      symbol: '₭',
      name: 'Laotian Kip',
      country: 'Laos',
      timezone: 'Asia/Vientiane',
      timezoneAbbr: 'ICT',
      minorUnit: 'Att'
    },

    // Major currencies (US, India, Hong Kong, China)
    'United States': {
      currency: 'USD',
      code: '840',
      symbol: '$',
      name: 'US Dollar',
      country: 'United States',
      timezone: 'America/New_York',
      timezoneAbbr: 'EST',
      minorUnit: 'Cent'
    },
    'India': {
      currency: 'INR',
      code: '356',
      symbol: '₹',
      name: 'Indian Rupee',
      country: 'India',
      timezone: 'Asia/Kolkata',
      timezoneAbbr: 'IST',
      minorUnit: 'Paisa'
    },
    'Hong Kong': {
      currency: 'HKD',
      code: '344',
      symbol: 'HK$',
      name: 'Hong Kong Dollar',
      country: 'Hong Kong',
      timezone: 'Asia/Hong_Kong',
      timezoneAbbr: 'HKT',
      minorUnit: 'Cent'
    },
    'China': {
      currency: 'CNY',
      code: '156',
      symbol: '¥',
      name: 'Chinese Yuan',
      country: 'China',
      timezone: 'Asia/Shanghai',
      timezoneAbbr: 'CST',
      minorUnit: 'Jiao'
    },

    // Other major world currencies
    'Australia': { currency: 'AUD', code: '036', symbol: 'A$', name: 'Australian Dollar', country: 'Australia', timezone: 'Australia/Sydney', timezoneAbbr: 'AEDT', minorUnit: 'Cent' },
    'Austria': { currency: 'EUR', code: '978', symbol: '€', name: 'Euro', country: 'Austria', timezone: 'Europe/Vienna', timezoneAbbr: 'CET', minorUnit: 'Cent' },
    'Belgium': { currency: 'EUR', code: '978', symbol: '€', name: 'Euro', country: 'Belgium', timezone: 'Europe/Brussels', timezoneAbbr: 'CET', minorUnit: 'Cent' },
    'Brazil': { currency: 'BRL', code: '986', symbol: 'R$', name: 'Brazilian Real', country: 'Brazil', timezone: 'America/Sao_Paulo', timezoneAbbr: 'BRT', minorUnit: 'Centavo' },
    'Canada': { currency: 'CAD', code: '124', symbol: 'C$', name: 'Canadian Dollar', country: 'Canada', timezone: 'America/Toronto', timezoneAbbr: 'EST', minorUnit: 'Cent' },
    'Chile': { currency: 'CLP', code: '152', symbol: '$', name: 'Chilean Peso', country: 'Chile', timezone: 'America/Santiago', timezoneAbbr: 'CLT', minorUnit: 'Centavo' },
    'Colombia': { currency: 'COP', code: '170', symbol: '$', name: 'Colombian Peso', country: 'Colombia', timezone: 'America/Bogota', timezoneAbbr: 'COT', minorUnit: 'Centavo' },
    'Czech Republic': { currency: 'CZK', code: '203', symbol: 'Kč', name: 'Czech Koruna', country: 'Czech Republic', timezone: 'Europe/Prague', timezoneAbbr: 'CET', minorUnit: 'Heller' },
    'Denmark': { currency: 'DKK', code: '208', symbol: 'kr', name: 'Danish Krone', country: 'Denmark', timezone: 'Europe/Copenhagen', timezoneAbbr: 'CET', minorUnit: 'Øre' },
    'Egypt': { currency: 'EGP', code: '818', symbol: 'LE', name: 'Egyptian Pound', country: 'Egypt', timezone: 'Africa/Cairo', timezoneAbbr: 'EET', minorUnit: 'Piastre' },
    'Finland': { currency: 'EUR', code: '978', symbol: '€', name: 'Euro', country: 'Finland', timezone: 'Europe/Helsinki', timezoneAbbr: 'EET', minorUnit: 'Cent' },
    'France': { currency: 'EUR', code: '978', symbol: '€', name: 'Euro', country: 'France', timezone: 'Europe/Paris', timezoneAbbr: 'CET', minorUnit: 'Cent' },
    'Germany': { currency: 'EUR', code: '978', symbol: '€', name: 'Euro', country: 'Germany', timezone: 'Europe/Berlin', timezoneAbbr: 'CET', minorUnit: 'Cent' },
    'Greece': { currency: 'EUR', code: '978', symbol: '€', name: 'Euro', country: 'Greece', timezone: 'Europe/Athens', timezoneAbbr: 'EET', minorUnit: 'Cent' },
    'Hungary': { currency: 'HUF', code: '348', symbol: 'Ft', name: 'Hungarian Forint', country: 'Hungary', timezone: 'Europe/Budapest', timezoneAbbr: 'CET', minorUnit: 'Fillér' },
    'Iceland': { currency: 'ISK', code: '352', symbol: 'kr', name: 'Icelandic Króna', country: 'Iceland', timezone: 'Atlantic/Reykjavik', timezoneAbbr: 'GMT', minorUnit: 'Eyrir' },
    'Ireland': { currency: 'EUR', code: '978', symbol: '€', name: 'Euro', country: 'Ireland', timezone: 'Europe/Dublin', timezoneAbbr: 'GMT', minorUnit: 'Cent' },
    'Israel': { currency: 'ILS', code: '376', symbol: '₪', name: 'Israeli New Shekel', country: 'Israel', timezone: 'Asia/Jerusalem', timezoneAbbr: 'IST', minorUnit: 'Agora' },
    'Italy': { currency: 'EUR', code: '978', symbol: '€', name: 'Euro', country: 'Italy', timezone: 'Europe/Rome', timezoneAbbr: 'CET', minorUnit: 'Cent' },
    'Japan': { currency: 'JPY', code: '392', symbol: '¥', name: 'Japanese Yen', country: 'Japan', timezone: 'Asia/Tokyo', timezoneAbbr: 'JST', minorUnit: 'Sen' },
    'Mexico': { currency: 'MXN', code: '484', symbol: '$', name: 'Mexican Peso', country: 'Mexico', timezone: 'America/Mexico_City', timezoneAbbr: 'CST', minorUnit: 'Centavo' },
    'Netherlands': { currency: 'EUR', code: '978', symbol: '€', name: 'Euro', country: 'Netherlands', timezone: 'Europe/Amsterdam', timezoneAbbr: 'CET', minorUnit: 'Cent' },
    'New Zealand': { currency: 'NZD', code: '554', symbol: 'NZ$', name: 'New Zealand Dollar', country: 'New Zealand', timezone: 'Pacific/Auckland', timezoneAbbr: 'NZDT', minorUnit: 'Cent' },
    'Norway': { currency: 'NOK', code: '578', symbol: 'kr', name: 'Norwegian Krone', country: 'Norway', timezone: 'Europe/Oslo', timezoneAbbr: 'CET', minorUnit: 'Øre' },
    'Poland': { currency: 'PLN', code: '985', symbol: 'zł', name: 'Polish Złoty', country: 'Poland', timezone: 'Europe/Warsaw', timezoneAbbr: 'CET', minorUnit: 'Grosz' },
    'Portugal': { currency: 'EUR', code: '978', symbol: '€', name: 'Euro', country: 'Portugal', timezone: 'Europe/Lisbon', timezoneAbbr: 'WET', minorUnit: 'Cent' },
    'Russia': { currency: 'RUB', code: '643', symbol: '₽', name: 'Russian Ruble', country: 'Russia', timezone: 'Europe/Moscow', timezoneAbbr: 'MSK', minorUnit: 'Kopeck' },
    'Saudi Arabia': { currency: 'SAR', code: '682', symbol: 'ر.س', name: 'Saudi Riyal', country: 'Saudi Arabia', timezone: 'Asia/Riyadh', timezoneAbbr: 'AST', minorUnit: 'Halala' },
    'South Africa': { currency: 'ZAR', code: '710', symbol: 'R', name: 'South African Rand', country: 'South Africa', timezone: 'Africa/Johannesburg', timezoneAbbr: 'SAST', minorUnit: 'Cent' },
    'South Korea': { currency: 'KRW', code: '410', symbol: '₩', name: 'South Korean Won', country: 'South Korea', timezone: 'Asia/Seoul', timezoneAbbr: 'KST', minorUnit: 'Jeon' },
    'Spain': { currency: 'EUR', code: '978', symbol: '€', name: 'Euro', country: 'Spain', timezone: 'Europe/Madrid', timezoneAbbr: 'CET', minorUnit: 'Cent' },
    'Sweden': { currency: 'SEK', code: '752', symbol: 'kr', name: 'Swedish Krona', country: 'Sweden', timezone: 'Europe/Stockholm', timezoneAbbr: 'CET', minorUnit: 'Öre' },
    'Switzerland': { currency: 'CHF', code: '756', symbol: 'Fr', name: 'Swiss Franc', country: 'Switzerland', timezone: 'Europe/Zurich', timezoneAbbr: 'CET', minorUnit: 'Rappen' },
    'Taiwan': { currency: 'TWD', code: '901', symbol: '$', name: 'New Taiwan Dollar', country: 'Taiwan', timezone: 'Asia/Taipei', timezoneAbbr: 'CST', minorUnit: 'Cent' },
    'Turkey': { currency: 'TRY', code: '949', symbol: '₺', name: 'Turkish Lira', country: 'Turkey', timezone: 'Europe/Istanbul', timezoneAbbr: 'TRT', minorUnit: 'Kuruş' },
    'United Arab Emirates': { currency: 'AED', code: '784', symbol: 'د.إ', name: 'UAE Dirham', country: 'United Arab Emirates', timezone: 'Asia/Dubai', timezoneAbbr: 'GST', minorUnit: 'Fils' },
    'United Kingdom': { currency: 'GBP', code: '826', symbol: '£', name: 'British Pound Sterling', country: 'United Kingdom', timezone: 'Europe/London', timezoneAbbr: 'GMT', minorUnit: 'Penny' },

    // Alternative country names and aliases
    'Viet Nam': {
      currency: 'VND',
      code: '704',
      symbol: '₫',
      name: 'Vietnamese Dong',
      country: 'Vietnam',
      timezone: 'Asia/Ho_Chi_Minh',
      timezoneAbbr: 'ICT',
      minorUnit: 'Hào'
    },
    'Brunei Darussalam': {
      currency: 'BND',
      code: '096',
      symbol: 'B$',
      name: 'Brunei Dollar',
      country: 'Brunei',
      timezone: 'Asia/Brunei',
      timezoneAbbr: 'BNT',
      minorUnit: 'Sen'
    },
    'Lao PDR': {
      currency: 'LAK',
      code: '418',
      symbol: '₭',
      name: 'Laotian Kip',
      country: 'Laos',
      timezone: 'Asia/Vientiane',
      timezoneAbbr: 'ICT',
      minorUnit: 'Att'
    },
    'USA': {
      currency: 'USD',
      code: '840',
      symbol: '$',
      name: 'US Dollar',
      country: 'United States',
      timezone: 'America/New_York',
      timezoneAbbr: 'EST',
      minorUnit: 'Cent'
    },
    'UK': {
      currency: 'GBP',
      code: '826',
      symbol: '£',
      name: 'British Pound Sterling',
      country: 'United Kingdom',
      timezone: 'Europe/London',
      timezoneAbbr: 'GMT',
      minorUnit: 'Penny'
    },
    'UAE': {
      currency: 'AED',
      code: '784',
      symbol: 'د.إ',
      name: 'UAE Dirham',
      country: 'United Arab Emirates',
      timezone: 'Asia/Dubai',
      timezoneAbbr: 'GST',
      minorUnit: 'Fils'
    },
    'HK': {
      currency: 'HKD',
      code: '344',
      symbol: 'HK$',
      name: 'Hong Kong Dollar',
      country: 'Hong Kong',
      timezone: 'Asia/Hong_Kong',
      timezoneAbbr: 'HKT',
      minorUnit: 'Cent'
    }
  };

  // Country aliases and patterns for address parsing
  private countryAliases: { [key: string]: string } = {
    'usa': 'United States',
    'us': 'United States',
    'america': 'United States',
    'united states of america': 'United States',
    'uk': 'United Kingdom',
    'britain': 'United Kingdom',
    'england': 'United Kingdom',
    'great britain': 'United Kingdom',
    'scotland': 'United Kingdom',
    'wales': 'United Kingdom',
    'northern ireland': 'United Kingdom',
    'uae': 'United Arab Emirates',
    'emirates': 'United Arab Emirates',
    'hk': 'Hong Kong',
    'hong kong sar': 'Hong Kong',
    'brunei darussalam': 'Brunei',
    'viet nam': 'Vietnam',
    'vietnam': 'Vietnam',
    'lao pdr': 'Laos',
    'burma': 'Myanmar',
    'korea': 'South Korea',
    'republic of korea': 'South Korea',
    'roc': 'Taiwan',
    'republic of china': 'Taiwan',
    'prc': 'China',
    'people\'s republic of china': 'China',
    'czech': 'Czech Republic',
    'holland': 'Netherlands',
    'south africa': 'South Africa',
    'rsa': 'South Africa'
  };

  // Postal code patterns for country detection
  private postalPatterns: { pattern: RegExp, country: string }[] = [
    { pattern: /\b\d{5}(-\d{4})?\b/, country: 'United States' }, // US ZIP codes
    { pattern: /\b[A-Z]\d[A-Z]\s*\d[A-Z]\d\b/i, country: 'Canada' }, // Canadian postal codes
    { pattern: /\b\d{5}\b/, country: 'Germany' }, // German postal codes
    { pattern: /\b(SW|EC|WC|N|NW|SE|E|W|CR|BR|DA|EN|HA|IG|KT|RM|SM|TW|UB|WD)\d+\s*\d[A-Z]{2}\b/i, country: 'United Kingdom' }, // UK postcodes
    { pattern: /\b\d{6}\b/, country: 'Singapore' }, // Singapore postal codes
    { pattern: /\b\d{5}\b/, country: 'Malaysia' }, // Malaysia postal codes
    { pattern: /\b\d{5}\b/, country: 'Thailand' }, // Thailand postal codes
    { pattern: /\b\d{4}\b/, country: 'Philippines' }, // Philippines postal codes
    { pattern: /\b\d{3}-\d{4}\b/, country: 'Japan' }, // Japan postal codes
    { pattern: /\b\d{5}\b/, country: 'Brazil' }, // Brazil CEP codes
    { pattern: /\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/i, country: 'United Kingdom' } // Alternative UK format
  ];

  /**
   * Get country, timezone and currency information by country name
   */
  getTimeCurrencyInfo(countryName: string): TimeCurrencyInfo {
    const normalizedCountry = countryName ? countryName.trim() : '';

    // Direct lookup
    if (this.timeCurrencyMapping[normalizedCountry]) {
      return this.timeCurrencyMapping[normalizedCountry];
    }

    // Case-insensitive lookup (Angular 2.3.1 compatible)
    const lowerCountry = normalizedCountry.toLowerCase();
    for (const key in this.timeCurrencyMapping) {
      if (this.timeCurrencyMapping.hasOwnProperty(key)) {
        if (key.toLowerCase() === lowerCountry) {
          return this.timeCurrencyMapping[key];
        }
      }
    }

    // Check aliases
    if (this.countryAliases[lowerCountry]) {
      return this.getTimeCurrencyInfo(this.countryAliases[lowerCountry]);
    }

    // Default to SGD (Singapore)
    return {
      currency: 'SGD',
      code: '702',
      symbol: 'S$',
      name: 'Singapore Dollar',
      country: 'Singapore',
      timezone: 'Asia/Singapore',
      timezoneAbbr: 'SGT',
      minorUnit: 'Cent'
    };
  }

  /**
   * Extract country from address string
   */
  extractCountryFromAddress(address: string): string | null {
    if (!address || address.trim() === '') {
      return null;
    }

    const addressLower = address.toLowerCase();
    const supportedCountries = this.getSupportedCountries();

    // Check for exact country name matches first
    for (let i = 0; i < supportedCountries.length; i++) {
      const country = supportedCountries[i];
      if (addressLower.indexOf(country.toLowerCase()) !== -1) {
        return country;
      }
    }

    // Check for country aliases
    for (const alias in this.countryAliases) {
      if (this.countryAliases.hasOwnProperty(alias)) {
        if (addressLower.indexOf(alias) !== -1) {
          return this.countryAliases[alias];
        }
      }
    }

    // Check for postal code patterns
    for (let i = 0; i < this.postalPatterns.length; i++) {
      const patternObj = this.postalPatterns[i];
      if (patternObj.pattern.test(address)) {
        return patternObj.country;
      }
    }

    return null;
  }

  /**
   * Get timezone by country name
   */
  getTimezoneByCountry(countryName: string): string {
    const info = this.getTimeCurrencyInfo(countryName);
    return info.timezone;
  }

  /**
   * Get timezone abbreviation by country name
   */
  getTimezoneAbbreviationByCountry(countryName: string): string {
    const info = this.getTimeCurrencyInfo(countryName);
    return info.timezoneAbbr;
  }

  /**
   * Get current date/time in specific timezone
   */
  getCurrentDateInTimezone(timezone: string): Date {
    const now = new Date();

    try {
      const localTime = new Date(now.toLocaleString("en-US", {
        timeZone: timezone
      }));
      return localTime;
    } catch (error) {
      console.warn('Invalid timezone:', timezone);
      return now;
    }
  }

  /**
   * Get current date/time in country's timezone
   */
  getCurrentDateInCountryTimezone(countryName: string): Date {
    const timezone = this.getTimezoneByCountry(countryName);
    return this.getCurrentDateInTimezone(timezone);
  }

  /**
   * Format date with timezone
   */
  formatDateWithTimezone(date: Date, timezone: string, options?: Intl.DateTimeFormatOptions): string {
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone
    };

    const formatOptions = options || defaultOptions;

    try {
      return date.toLocaleString('en-US', formatOptions);
    } catch (error) {
      console.warn('Error formatting date with timezone:', timezone);
      return date.toLocaleString('en-US', defaultOptions);
    }
  }

  /**
   * Get supported countries
   */
  getSupportedCountries(): string[] {
    const countries: string[] = [];
    const seen: { [key: string]: boolean } = {};

    for (const key in this.timeCurrencyMapping) {
      if (this.timeCurrencyMapping.hasOwnProperty(key)) {
        const country = this.timeCurrencyMapping[key].country;
        if (!seen[country]) {
          seen[country] = true;
          countries.push(country);
        }
      }
    }

    return countries.sort();
  }

  /**
   * Get all supported timezones
   */
  getSupportedTimezones(): string[] {
    const timezones: string[] = [];
    const seen: { [key: string]: boolean } = {};

    for (const key in this.timeCurrencyMapping) {
      if (this.timeCurrencyMapping.hasOwnProperty(key)) {
        const timezone = this.timeCurrencyMapping[key].timezone;
        if (!seen[timezone]) {
          seen[timezone] = true;
          timezones.push(timezone);
        }
      }
    }

    return timezones.sort();
  }

  /**
   * Get currency info by currency code (INR, SGD, USD, etc.)
   */
  getCurrencyInfoByCurrencyCode(currencyCode: string): TimeCurrencyInfo | null {
    for (const key in this.timeCurrencyMapping) {
      if (this.timeCurrencyMapping.hasOwnProperty(key)) {
        const currency = this.timeCurrencyMapping[key];
        if (currency.currency === currencyCode) {
          return currency;
        }
      }
    }

    // Default to SGD (Singapore)
    return {
      currency: 'SGD',
      code: '702',
      symbol: 'S$',
      name: 'Singapore Dollar',
      country: 'Singapore',
      timezone: 'Asia/Singapore',
      timezoneAbbr: 'SGT',
      minorUnit: 'Cent'
    };
  }

  /**
   * Get ASEAN currencies only (10 unique currencies)
   */
  getASEANCurrencies(): TimeCurrencyInfo[] {
    const aseanCountries = [
      'Indonesia', 'Singapore', 'Malaysia', 'Thailand', 'Vietnam',
      'Philippines', 'Myanmar', 'Brunei', 'Cambodia', 'Laos'
    ];

    const aseanCurrencies: TimeCurrencyInfo[] = [];
    const seen: { [key: string]: boolean } = {};

    for (let i = 0; i < aseanCountries.length; i++) {
      const currencyInfo = this.getTimeCurrencyInfo(aseanCountries[i]);
      if (!seen[currencyInfo.currency]) {
        seen[currencyInfo.currency] = true;
        aseanCurrencies.push(currencyInfo);
      }
    }

    return aseanCurrencies.sort(function(a, b) {
      return a.currency.localeCompare(b.currency);
    });
  }

  /**
   * Get major currencies: US, India, Hong Kong, China + All ASEAN Countries
   */
  getMajorCurrencies(): TimeCurrencyInfo[] {
    const majorCurrencyList = [
      'USD', 'INR', 'HKD', 'CNY',
      'IDR', 'SGD', 'MYR', 'THB', 'VND', 'PHP', 'MMK', 'BND', 'KHR', 'LAK'
    ];

    const allCurrencies = this.getAllCurrencies();
    const majorCurrencies: TimeCurrencyInfo[] = [];

    for (let i = 0; i < allCurrencies.length; i++) {
      if (majorCurrencyList.indexOf(allCurrencies[i].currency) !== -1) {
        majorCurrencies.push(allCurrencies[i]);
      }
    }

    const order = ['USD', 'INR', 'HKD', 'CNY', 'BND', 'IDR', 'KHR', 'LAK', 'MYR', 'MMK', 'PHP', 'SGD', 'THB', 'VND'];
    return majorCurrencies.sort(function(a, b) {
      return order.indexOf(a.currency) - order.indexOf(b.currency);
    });
  }

  /**
   * Get all available currencies (unique)
   */
  getAllCurrencies(): TimeCurrencyInfo[] {
    const uniqueCurrencies: TimeCurrencyInfo[] = [];
    const seen: { [key: string]: boolean } = {};

    for (const key in this.timeCurrencyMapping) {
      if (this.timeCurrencyMapping.hasOwnProperty(key)) {
        const currencyInfo = this.timeCurrencyMapping[key];
        if (!seen[currencyInfo.currency]) {
          seen[currencyInfo.currency] = true;
          uniqueCurrencies.push(currencyInfo);
        }
      }
    }

    return uniqueCurrencies.sort(function(a, b) {
      return a.currency.localeCompare(b.currency);
    });
  }

  /**
   * Get currency symbol by currency code
   */
  getCurrencySymbol(currencyCode: string): string {
    for (const key in this.timeCurrencyMapping) {
      if (this.timeCurrencyMapping.hasOwnProperty(key)) {
        const currency = this.timeCurrencyMapping[key];
        if (currency.currency === currencyCode) {
          return currency.symbol;
        }
      }
    }
    return currencyCode;
  }

  /**
   * Get currency name by currency code
   */
  getCurrencyName(currencyCode: string): string {
    for (const key in this.timeCurrencyMapping) {
      if (this.timeCurrencyMapping.hasOwnProperty(key)) {
        const currency = this.timeCurrencyMapping[key];
        if (currency.currency === currencyCode) {
          return currency.name;
        }
      }
    }
    return currencyCode;
  }

  /**
   * Get currency by ISO code
   */
  getCurrencyByCode(isoCode: string): TimeCurrencyInfo | null {
    for (const key in this.timeCurrencyMapping) {
      if (this.timeCurrencyMapping.hasOwnProperty(key)) {
        const currency = this.timeCurrencyMapping[key];
        if (currency.code === isoCode) {
          return currency;
        }
      }
    }
    return null;
  }

  /**
   * Search currencies by name, code, or country (Angular 2.3.1 compatible)
   */
  searchCurrencies(searchTerm: string): TimeCurrencyInfo[] {
    const term = searchTerm.toLowerCase();
    const results: TimeCurrencyInfo[] = [];
    const allCurrencies = this.getAllCurrencies();

    for (let i = 0; i < allCurrencies.length; i++) {
      const currency = allCurrencies[i];
      if (currency.currency.toLowerCase().indexOf(term) !== -1 ||
          currency.name.toLowerCase().indexOf(term) !== -1 ||
          currency.country.toLowerCase().indexOf(term) !== -1 ||
          currency.symbol.indexOf(term) !== -1) {
        results.push(currency);
      }
    }

    return results;
  }

  /**
   * Search countries by name or alias (Angular 2.3.1 compatible)
   */
  searchCountries(searchTerm: string): string[] {
    const term = searchTerm.toLowerCase();
    const results: string[] = [];
    const countries = this.getSupportedCountries();

    for (let i = 0; i < countries.length; i++) {
      const country = countries[i];
      if (country.toLowerCase().indexOf(term) !== -1) {
        results.push(country);
      }
    }

    // Check aliases
    for (const alias in this.countryAliases) {
      if (this.countryAliases.hasOwnProperty(alias)) {
        if (alias.indexOf(term) !== -1) {
          const country = this.countryAliases[alias];
          if (results.indexOf(country) === -1) {
            results.push(country);
          }
        }
      }
    }

    return results.sort();
  }

  /**
   * Validate if timezone is supported
   */
  isTimezoneSupported(timezone: string): boolean {
    const supportedTimezones = this.getSupportedTimezones();
    return supportedTimezones.indexOf(timezone) !== -1;
  }

  /**
   * Validate if country is supported
   */
  isCountrySupported(countryName: string): boolean {
    return this.getTimeCurrencyInfo(countryName).country !== 'Singapore' ||
           countryName.toLowerCase() === 'singapore';
  }

  /**
   * Validate if currency code is supported
   */
  isCurrencySupported(currencyCode: string): boolean {
    return this.getCurrencyInfoByCurrencyCode(currencyCode) !== null;
  }

  /**
   * Get countries by currency code
   */
  getCountriesByCurrency(currencyCode: string): string[] {
    const countries: string[] = [];

    for (const key in this.timeCurrencyMapping) {
      if (this.timeCurrencyMapping.hasOwnProperty(key)) {
        const info = this.timeCurrencyMapping[key];
        if (info.currency === currencyCode && countries.indexOf(info.country) === -1) {
          countries.push(info.country);
        }
      }
    }

    return countries.sort();
  }

  /**
   * Get countries by timezone
   */
  getCountriesByTimezone(timezone: string): string[] {
    const countries: string[] = [];

    for (const key in this.timeCurrencyMapping) {
      if (this.timeCurrencyMapping.hasOwnProperty(key)) {
        const info = this.timeCurrencyMapping[key];
        if (info.timezone === timezone && countries.indexOf(info.country) === -1) {
          countries.push(info.country);
        }
      }
    }

    return countries.sort();
  }

  // Backward compatibility methods (deprecated but maintained)
  /**
   * @deprecated Use getTimeCurrencyInfo instead
   */
  getCurrencyInfo(countryName: string): TimeCurrencyInfo {
    return this.getTimeCurrencyInfo(countryName);
  }

  /**
   * @deprecated Use getTimeCurrencyInfo instead
   */
  getCountryTimeCurrencyInfo(countryName: string): TimeCurrencyInfo {
    return this.getTimeCurrencyInfo(countryName);
  }

  /**
 * Get pincode/postal code validation rules for ASEAN + specified countries
 * Returns validation rules with patterns, lengths, and character allowances
 */
getPincodeRules(): any[] {
  return [
    // ASEAN Countries
    {
      countries: ['Singapore'],
      minLength: 6,
      maxLength: 6,
      allowAlpha: false,
      allowNumeric: true,
      allowSpaces: false,
      allowHyphens: false,
      pattern: /^[0-9]{6}$/,
      description: '6-digit postal code'
    },
    {
      countries: ['Malaysia'],
      minLength: 5,
      maxLength: 5,
      allowAlpha: false,
      allowNumeric: true,
      allowSpaces: false,
      allowHyphens: false,
      pattern: /^[0-9]{5}$/,
      description: '5-digit postcode'
    },
    {
      countries: ['Thailand'],
      minLength: 5,
      maxLength: 5,
      allowAlpha: false,
      allowNumeric: true,
      allowSpaces: false,
      allowHyphens: false,
      pattern: /^[0-9]{5}$/,
      description: '5-digit postal code'
    },
    {
      countries: ['Indonesia'],
      minLength: 5,
      maxLength: 5,
      allowAlpha: false,
      allowNumeric: true,
      allowSpaces: false,
      allowHyphens: false,
      pattern: /^[0-9]{5}$/,
      description: '5-digit postal code'
    },
    {
      countries: ['Philippines'],
      minLength: 4,
      maxLength: 4,
      allowAlpha: false,
      allowNumeric: true,
      allowSpaces: false,
      allowHyphens: false,
      pattern: /^[0-9]{4}$/,
      description: '4-digit ZIP code'
    },
    {
      countries: ['Vietnam', 'Viet Nam'],
      minLength: 6,
      maxLength: 6,
      allowAlpha: false,
      allowNumeric: true,
      allowSpaces: false,
      allowHyphens: false,
      pattern: /^[0-9]{6}$/,
      description: '6-digit postal code'
    },
    {
      countries: ['Myanmar'],
      minLength: 5,
      maxLength: 5,
      allowAlpha: false,
      allowNumeric: true,
      allowSpaces: false,
      allowHyphens: false,
      pattern: /^[0-9]{5}$/,
      description: '5-digit postal code'
    },
    {
      countries: ['Cambodia'],
      minLength: 5,
      maxLength: 6,
      allowAlpha: false,
      allowNumeric: true,
      allowSpaces: false,
      allowHyphens: false,
      pattern: /^[0-9]{5,6}$/,
      description: '5-6 digit postal code'
    },
    {
      countries: ['Laos', 'Lao PDR'],
      minLength: 5,
      maxLength: 5,
      allowAlpha: false,
      allowNumeric: true,
      allowSpaces: false,
      allowHyphens: false,
      pattern: /^[0-9]{5}$/,
      description: '5-digit postal code'
    },
    {
      countries: ['Brunei', 'Brunei Darussalam'],
      minLength: 6,
      maxLength: 6,
      allowAlpha: true,
      allowNumeric: true,
      allowSpaces: false,
      allowHyphens: false,
      pattern: /^[A-Z]{2}[0-9]{4}$/,
      description: '2 letters followed by 4 digits (e.g., BA1234)'
    },

    // Additional Specified Countries
    {
      countries: ['India'],
      minLength: 6,
      maxLength: 6,
      allowAlpha: false,
      allowNumeric: true,
      allowSpaces: false,
      allowHyphens: false,
      pattern: /^[1-9][0-9]{5}$/,
      description: '6-digit numeric PIN code (not starting with 0)'
    },
    {
      countries: ['Hong Kong', 'HK'],
      minLength: 0,
      maxLength: 0,
      allowAlpha: false,
      allowNumeric: false,
      allowSpaces: false,
      allowHyphens: false,
      pattern: /^$/,
      description: 'No postal code required'
    },
    {
      countries: ['United States', 'USA', 'US'],
      minLength: 5,
      maxLength: 10,
      allowAlpha: false,
      allowNumeric: true,
      allowSpaces: false,
      allowHyphens: true,
      pattern: /^[0-9]{5}(-[0-9]{4})?$/,
      description: '5-digit ZIP or 9-digit ZIP+4 format (12345-6789)'
    },
    {
      countries: ['Canada'],
      minLength: 6,
      maxLength: 7,
      allowAlpha: true,
      allowNumeric: true,
      allowSpaces: true,
      allowHyphens: false,
      pattern: /^[A-Za-z][0-9][A-Za-z][ ]?[0-9][A-Za-z][0-9]$/,
      description: 'Canadian postal code (A1A 1A1 or A1A1A1)'
    },
    {
      countries: ['United Kingdom', 'UK', 'Britain'],
      minLength: 5,
      maxLength: 8,
      allowAlpha: true,
      allowNumeric: true,
      allowSpaces: true,
      allowHyphens: false,
      pattern: /^[A-Za-z]{1,2}[0-9]{1,2}[A-Za-z]?[ ]?[0-9][A-Za-z]{2}$/,
      description: 'UK postcode (e.g., SW1A 1AA, M1 1AA)'
    },
    {
      countries: ['Australia'],
      minLength: 4,
      maxLength: 4,
      allowAlpha: false,
      allowNumeric: true,
      allowSpaces: false,
      allowHyphens: false,
      pattern: /^[0-9]{4}$/,
      description: '4-digit postcode'
    }
  ];
}

/**
 * Get pincode validation rule for a specific country
 * @param countryName - Name of the country to get rules for
 * @returns Pincode rule object or null if not found
 */
getPincodeRuleForCountry(countryName: string): any | null {
  const rules = this.getPincodeRules();
  const normalizedCountry = countryName.trim();

  // Use traditional for loop for Angular 2.3.1 compatibility
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i];
    for (let j = 0; j < rule.countries.length; j++) {
      if (rule.countries[j].toLowerCase() === normalizedCountry.toLowerCase()) {
        return rule;
      }
    }
  }

  // Check aliases
  const lowerCountry = normalizedCountry.toLowerCase();
  if (this.countryAliases[lowerCountry]) {
    return this.getPincodeRuleForCountry(this.countryAliases[lowerCountry]);
  }

  return null;
}

/**
 * Validate pincode format for a specific country
 * @param pincode - The pincode to validate
 * @param countryName - Name of the country
 * @returns Boolean indicating if pincode is valid
 */
validatePincodeForCountry(pincode: string, countryName: string): boolean {
  if (!pincode || !countryName) {
    return false;
  }

  const rule = this.getPincodeRuleForCountry(countryName);
  if (!rule) {
    // Default validation for unsupported countries
    return /^[A-Za-z0-9\s\-]{3,10}$/.test(pincode.trim());
  }

  // Special case for Hong Kong (no postal code required)
  if (rule.minLength === 0 && rule.maxLength === 0) {
    return pincode.trim() === '';
  }

  const cleanPincode = pincode.trim();

  // Check length
  if (cleanPincode.length < rule.minLength || cleanPincode.length > rule.maxLength) {
    return false;
  }

  // Check pattern if specified
  if (rule.pattern && !rule.pattern.test(cleanPincode)) {
    return false;
  }

  // Character type validation
  for (let i = 0; i < cleanPincode.length; i++) {
    const char = cleanPincode.charAt(i);

    if (/[0-9]/.test(char)) {
      if (!rule.allowNumeric) return false;
    } else if (/[A-Za-z]/.test(char)) {
      if (!rule.allowAlpha) return false;
    } else if (char === ' ') {
      if (!rule.allowSpaces) return false;
    } else if (char === '-') {
      if (!rule.allowHyphens) return false;
    } else {
      // Invalid character
      return false;
    }
  }

  return true;
}

/**
 * Check if a country requires postal code
 * @param countryName - Name of the country to check
 * @returns Boolean indicating if postal code is required
 */
isPostalCodeRequired(countryName: string): boolean {
  const rule = this.getPincodeRuleForCountry(countryName);
  if (!rule) {
    return true; // Default to requiring postal code
  }

  // Countries with minLength and maxLength of 0 don't require postal codes
  return !(rule.minLength === 0 && rule.maxLength === 0);
}

}



// WEBPACK FOOTER //
// ./src/app/facility/time-currency.service.ts